import React, { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	TextField,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { warehouseService } from '../../services/warehouseService'
import UserLayout from '../../layouts/UserLayout'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStores, selectStores, selectStoresLoading } from '../../redux/slices/storeSlice'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import LanguageIcon from '@mui/icons-material/Language'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'

function CategoryDetail() {
	const dispatch = useDispatch()
	const stores = useSelector(selectStores)
	const loading = useSelector(selectStoresLoading)
	const [selectedStore, setSelectedStore] = useState(null)
	const [stockData, setStockData] = useState(null)
	const [importData, setImportData] = useState({
		quantity: '',
		note: '',
	})

	const [dateRange, setDateRange] = useState({
		firstTimePick: {
			date: '5',
			month: '3',
			year: '2025',
		},
		secondTimePick: {
			date: '5',
			month: '3',
			year: '2025',
		},
	})

	const navigate = useNavigate()

	const [openImportDialog, setOpenImportDialog] = useState(false)

	// Chỉ set selectedStore khi stores có dữ liệu và chưa được chọn
	useEffect(() => {
		if (stores.length > 0 && !selectedStore) {
			setSelectedStore(stores[0]._id)
		}
	}, [stores, selectedStore])

	// Chỉ giữ lại API POST để lấy dữ liệu theo ngày tháng
	useEffect(() => {
		const fetchData = async () => {
			if (!selectedStore) return

			try {
				const response = await apiBase.post(`${api.ADMIN_STOCK_HISTORY_BY_DATE}/${selectedStore}`, {
					firstTimePick: {
						date: dateRange.firstTimePick.date,
						month: dateRange.firstTimePick.month,
						year: dateRange.firstTimePick.year,
					},
					secondTimePick: {
						date: dateRange.secondTimePick.date,
						month: dateRange.secondTimePick.month,
						year: dateRange.secondTimePick.year,
					},
				})

				if (Array.isArray(response)) {
					const lastItem = response[response.length - 1]
					const totalBalance = response.reduce((sum, item) => sum + (item.balanceStore || 0), 0)

					setStockData({
						...lastItem,
						balanceStore: totalBalance,
					})
				}
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}

		fetchData()
	}, [selectedStore, dateRange.firstTimePick, dateRange.secondTimePick])

	useEffect(() => {
		dispatch(fetchStores())
	}, [dispatch])

	// Handle import stock
	const handleImportStock = async () => {
		try {
			if (!importData.quantity || importData.quantity <= 0) {
				alert('Vui lòng nhập số lượng hợp lệ')
				return
			}

			const data = {
				quantity: importData.quantity,
				note: importData.note || '',
			}

			await warehouseService.importStock(data)

			setImportData({ quantity: '', note: '' })
			setOpenImportDialog(false)

			alert('Nhập kho thành công!')
		} catch (error) {
			alert('Có lỗi xảy ra khi nhập kho!')
		}
	}

	const handleDateChange = (timePickType, field, value) => {
		setDateRange((prev) => ({
			...prev,
			[timePickType]: {
				...prev[timePickType],
				[field]: value,
			},
		}))
	}

	// Hiển thị dữ liệu từ API
	const renderStockData = () => {
		if (!stockData) return null

		return (
			<Box sx={{ mt: 4 }}>
				{/* First Line */}
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
					<ArrowUpwardIcon sx={{ color: '#00a65a', mr: 1 }} />
					<Typography sx={{ color: '#00a65a', fontWeight: 'medium' }}>
						(+{stockData.request?.quantity || stockData.quantity})
					</Typography>
					<Typography sx={{ mx: 1 }}>nhập từ</Typography>
					<LanguageIcon sx={{ color: '#1976d2', mr: 1 }} />
					<Typography sx={{ fontWeight: 'bold' }}>{stockData.user?.name || 'KHO TỔNG'}</Typography>
				</Box>

				{/* Second Line */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography>Đơn giá</Typography>
					<Typography sx={{ color: '#00a65a', fontWeight: 'medium' }}>
						{stockData.unitPrice?.toLocaleString()}đ
					</Typography>
					<Typography>. Doanh thu</Typography>
					<Typography sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
						{(stockData.quantity * stockData.unitPrice)?.toLocaleString()}đ
					</Typography>
				</Box>

				{/* Third Line */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Typography sx={{ mr: 1 }}>Tồn:</Typography>
						<Typography sx={{ fontWeight: 'bold' }}>{stockData.balanceStore?.toFixed(1)}Kg</Typography>
					</Box>
					<Typography sx={{ color: '#00a65a', fontStyle: 'italic' }}>-{stockData.timeStamp}-</Typography>
				</Box>
			</Box>
		)
	}

	const handleCloseDialog = () => {
		setImportData({ quantity: '', note: '' })
		setOpenImportDialog(false)
	}

	return (
		<UserLayout>
			<Box>
				<Box sx={{ maxWidth: 992, margin: '0 auto' }}>
					<Box
						sx={{
							bgcolor: 'background.paper',
							borderRadius: 2,
							boxShadow: 1,
							overflow: 'hidden',
							border: '1px solid rgba(0, 0, 0, 0.12)',
							p: 4,
						}}>
						{/* Content */}
						<Box>
							{/* Tồn kho */}
							<Box sx={{ mb: 3 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
									<Typography variant='h6'>Tồn kho</Typography>
									<Typography sx={{ color: '#00a65a' }}>{(stockData?.balanceStore || 0).toFixed(1)}kg</Typography>
								</Box>

								{/* Nhập nem sống */}
								<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
									<Button
										variant='outlined'
										onClick={() => navigate('/admin/stock/history')}
										sx={{
											flex: 1,
											color: '#00a65a',
											borderColor: '#00a65a',
											'&:hover': {
												borderColor: '#00a65a',
												bgcolor: 'rgba(0, 166, 90, 0.1)',
											},
										}}>
										Biến động
									</Button>
									<Button
										variant='outlined'
										onClick={() => navigate('/admin/stock/forecast')}
										sx={{
											flex: 1,
											color: '#dd4b39',
											borderColor: '#dd4b39',
											'&:hover': {
												borderColor: '#dd4b39',
												bgcolor: 'rgba(221, 75, 57, 0.1)',
											},
										}}>
										Dự báo
									</Button>
								</Box>
							</Box>

							{/* Nhập nem sống */}
							<Typography variant='h6' sx={{ mb: 2 }}>
								Nhập nem sống
							</Typography>
							<Box sx={{ mb: 3 }}>
								<TextField
									fullWidth
									placeholder='Khối lượng nhập (kg)'
									value={importData.quantity}
									onChange={(e) => setImportData({ ...importData, quantity: e.target.value })}
									sx={{ mb: 2 }}
								/>
								<TextField
									fullWidth
									placeholder='Ghi chú'
									value={importData.note}
									onChange={(e) => setImportData({ ...importData, note: e.target.value })}
									sx={{ mb: 2 }}
								/>
								<Button
									fullWidth
									variant='contained'
									onClick={handleImportStock}
									sx={{
										bgcolor: '#00a65a',
										'&:hover': {
											bgcolor: '#008d4c',
										},
									}}>
									NHẬP KHO
								</Button>
							</Box>

							{/* Yêu cầu xuất hàng */}
							<Typography variant='h6' sx={{ mb: 1 }}>
								Yêu cầu xuất hàng
							</Typography>
							<Typography sx={{ color: '#999', mb: 3 }}>(Chưa có dữ liệu)</Typography>

							{/* Thống kê giá bán chi nhánh */}
							<Box sx={{ mb: 4 }}>
								<Typography variant='h6' sx={{ mb: 2 }}>
									Thống kê giá bán chi nhánh
								</Typography>

								{/* Store Selection Buttons */}
								<Box
									sx={{
										display: 'flex',
										gap: 1,
										mb: 3,
										flexWrap: 'wrap',
									}}>
									{loading ? (
										<Typography>Loading stores...</Typography>
									) : (
										stores.map((store) => (
											<Button
												key={store._id}
												variant={selectedStore === store._id ? 'contained' : 'outlined'}
												sx={{
													minWidth: 'auto',
													textTransform: 'none',
													px: 2,
													color: selectedStore === store._id ? 'white' : '#00a65a',
													backgroundColor: selectedStore === store._id ? '#00a65a' : 'transparent',
													borderColor: '#00a65a',
													'&:hover': {
														backgroundColor: selectedStore === store._id ? '#009551' : 'rgba(0, 166, 90, 0.08)',
														borderColor: '#00a65a',
													},
												}}
												onClick={() => setSelectedStore(store._id)}>
												{store.name}
											</Button>
										))
									)}
								</Box>

								{/* Date Range Selection */}
								<Box sx={{ mb: 3 }}>
									<Typography variant='subtitle1' sx={{ mb: 1 }}>
										Từ:
									</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: '1fr 1fr 1fr',
											gap: 2,
										}}>
										<FormControl fullWidth size='small'>
											<InputLabel>Ngày</InputLabel>
											<Select
												value={dateRange.firstTimePick.date}
												onChange={(e) => handleDateChange('firstTimePick', 'date', e.target.value)}
												label='Ngày'>
												{[...Array(31)].map((_, i) => (
													<MenuItem key={i + 1} value={String(i + 1)}>
														{i + 1}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl fullWidth size='small'>
											<InputLabel>Tháng</InputLabel>
											<Select
												value={dateRange.firstTimePick.month}
												onChange={(e) => handleDateChange('firstTimePick', 'month', e.target.value)}
												label='Tháng'>
												{[...Array(12)].map((_, i) => (
													<MenuItem key={i + 1} value={String(i + 1)}>
														{i + 1}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl fullWidth size='small'>
											<InputLabel>Năm</InputLabel>
											<Select
												value={dateRange.firstTimePick.year}
												onChange={(e) => handleDateChange('firstTimePick', 'year', e.target.value)}
												label='Năm'>
												{[2024, 2025].map((year) => (
													<MenuItem key={year} value={String(year)}>
														{year}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>
								</Box>

								<Box sx={{ mb: 3 }}>
									<Typography variant='subtitle1' sx={{ mb: 1 }}>
										Đến:
									</Typography>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: '1fr 1fr 1fr',
											gap: 2,
										}}>
										<FormControl fullWidth size='small'>
											<InputLabel>Ngày</InputLabel>
											<Select
												value={dateRange.secondTimePick.date}
												onChange={(e) => handleDateChange('secondTimePick', 'date', e.target.value)}
												label='Ngày'>
												{[...Array(31)].map((_, i) => (
													<MenuItem key={i + 1} value={String(i + 1)}>
														{i + 1}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl fullWidth size='small'>
											<InputLabel>Tháng</InputLabel>
											<Select
												value={dateRange.secondTimePick.month}
												onChange={(e) => handleDateChange('secondTimePick', 'month', e.target.value)}
												label='Tháng'>
												{[...Array(12)].map((_, i) => (
													<MenuItem key={i + 1} value={String(i + 1)}>
														{i + 1}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl fullWidth size='small'>
											<InputLabel>Năm</InputLabel>
											<Select
												value={dateRange.secondTimePick.year}
												onChange={(e) => handleDateChange('secondTimePick', 'year', e.target.value)}
												label='Năm'>
												{[2024, 2025].map((year) => (
													<MenuItem key={year} value={String(year)}>
														{year}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>
								</Box>
							</Box>

							{/* History Items */}
							<Box sx={{ mt: 3 }}>{renderStockData()}</Box>
						</Box>

						{/* Import Button */}
						<Button
							variant='contained'
							onClick={() => setOpenImportDialog(true)}
							sx={{
								bgcolor: '#00a65a',
								'&:hover': { bgcolor: '#00a65a' },
								mb: 2,
							}}>
							Nhập kho
						</Button>

						{/* Import Dialog */}
						<Dialog
							open={openImportDialog}
							onClose={handleCloseDialog}
							PaperProps={{
								component: 'form',
								onSubmit: (e) => {
									e.preventDefault()
									handleImportStock()
								},
							}}>
							<DialogTitle>Nhập kho</DialogTitle>
							<DialogContent>
								<TextField
									autoFocus
									margin='dense'
									label='Số lượng (kg)'
									type='number'
									fullWidth
									value={importData.quantity}
									onChange={(e) => setImportData({ ...importData, quantity: e.target.value })}
									sx={{ mb: 2 }}
								/>
								<TextField
									margin='dense'
									label='Ghi chú'
									fullWidth
									multiline
									rows={3}
									value={importData.note}
									onChange={(e) => setImportData({ ...importData, note: e.target.value })}
								/>
							</DialogContent>
							<DialogActions>
								<Button type='button' onClick={handleCloseDialog} sx={{ color: '#dd4b39' }}>
									Hủy
								</Button>
								<Button type='submit' sx={{ color: '#00a65a' }}>
									Xác nhận
								</Button>
							</DialogActions>
						</Dialog>
					</Box>
				</Box>
			</Box>
		</UserLayout>
	)
}

export default CategoryDetail
